import React, { useState, useEffect } from 'react';
import { BaseModal } from '@airclou/ui';
import { Button, Input, LoadingButton } from '@airclou/ui';
import { Trash2, AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { TenantErasurePreview } from '../../../services/api';
import { ErasureStoreList } from './ErasureStoreList';

interface DeleteOrganizationModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Receives the typed confirmation so the server can check it too. */
  onConfirm: (confirmName: string) => Promise<void>;
  organizationName: string;
  organizationEmail?: string;
  isDeleting?: boolean;
  /** Server-named failure, already translated by the page. */
  errorMessage?: string | null;
  /**
   * What deleting this organization would erase, counted while it is still
   * live. Null while it loads, and null for good if it could not be drawn.
   *
   * It is deliberately NOT a precondition of deleting. An owner must be able to
   * close their account when a service is down, so a missing summary says so
   * and the button still works. See docs/settings-open-items.md §7.
   */
  erasurePreview?: TenantErasurePreview | null;
  isPreviewLoading?: boolean;
  /** True when the count could not be drawn at all — say so, don't show zeros. */
  previewFailed?: boolean;
}

export const DeleteOrganizationModal: React.FC<DeleteOrganizationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  organizationName,
  organizationEmail,
  isDeleting = false,
  errorMessage = null,
  erasurePreview = null,
  isPreviewLoading = false,
  previewFailed = false,
}) => {
  const { t, i18n } = useTranslation('dashboard');
  const [confirmationInput, setConfirmationInput] = useState<string>('');
  const formatCount = (n: number) => new Intl.NumberFormat(i18n.language || undefined).format(n);
  const accounts = erasurePreview?.accounts ?? null;
  const you = accounts?.you ?? null;

  // The confirmation is the organization's own NAME, not a random phrase this
  // component invented. A phrase the client generates and the client marks
  // proves nothing once the request leaves the browser — the server could not
  // check it, so it was theatre. The name is something only someone looking at
  // the right organization knows, and `DELETE /api/tenants/:id` verifies it.

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setConfirmationInput('');
    }
  }, [isOpen]);

  const handleConfirm = async () => {
    if (!isConfirmationValid) {
      return;
    }

    try {
      await onConfirm(confirmationInput.trim());
    } catch (error) {
      console.error('Organization deletion failed:', error);
    }
  };

  const handleCancel = () => {
    setConfirmationInput('');
    onClose();
  };

  const isConfirmationValid =
    confirmationInput.trim().toLowerCase() === organizationName.trim().toLowerCase() &&
    organizationName.trim().length > 0;

  const footer = (
    <div className="flex items-center justify-end gap-3">
      <Button 
        variant="outline" 
        onClick={handleCancel}
        disabled={isDeleting}
      >
        {t('common.cancel')}
      </Button>
      <LoadingButton
        onClick={handleConfirm}
        loading={isDeleting}
        loadingText={t('common.deleting')}
        disabled={!isConfirmationValid}
        className="bg-red-600 hover:bg-red-700 dark:bg-red-500 dark:text-gray-950 dark:hover:bg-red-400 text-white"
      >
        <Trash2 className="h-4 w-4 mr-2" />
        {t('deleteOrg.confirmDeletion')}
      </LoadingButton>
    </div>
  );

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={handleCancel}
      title={
        <div className="flex items-center gap-2">
          <Trash2 className="h-5 w-5 text-red-600 dark:text-red-400" />
          <span>{t('deleteOrg.confirmTitle')}</span>
        </div>
      }
      size="lg"
      footer={footer}
      closeOnBackdrop={!isDeleting}
      closeOnEscape={!isDeleting}
      contentClassName="p-6"
    >
      <div className="space-y-4">
        {/* What actually happens, in the order it happens. The previous copy
            listed five things this would delete — playbooks, forms, users,
            billing, integrations — of which only the billing was true. Nothing
            is erased at this point, and telling someone their data is gone when
            it is still on disk is the one claim here worth getting right. */}
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-red-800 dark:text-red-200 mb-2">
                {t('deleteOrg.whatHappensTitle')}
              </p>
              <ul className="list-disc list-inside space-y-1 text-red-700 dark:text-red-300">
                <li>{t('deleteOrg.effectAccess')}</li>
                <li>{t('deleteOrg.effectBilling')}</li>
                <li>{t('deleteOrg.effectData')}</li>
              </ul>
              <p className="text-red-700 dark:text-red-300 mt-2">
                {t('deleteOrg.effectRestore')}
              </p>
            </div>
          </div>
        </div>

        {/* The blast radius, BEFORE the button. Typing the organization name
            proves the owner knows WHICH organization; these figures are what
            tell them WHAT IS IN IT. The counts used to appear only after the
            delete had already happened, which is the wrong order for the
            largest irreversible action a tenant admin can take. */}
        <section className="text-sm">
          <h3 className="font-medium text-foreground mb-2">
            {t('deleteOrg.whatWillBeErasedLater')}
          </h3>
          {previewFailed && !isPreviewLoading ? (
            <p className="text-gray-500 dark:text-gray-400">{t('deleteOrg.previewUnavailableBeforeDelete')}</p>
          ) : (
            <>
              <ErasureStoreList preview={erasurePreview} isLoading={isPreviewLoading} />
              {accounts !== null && !isPreviewLoading && (
                <p className="mt-2 text-gray-700 dark:text-gray-300">
                  {t('deleteOrg.accountsDeleted', { count: accounts.toDelete, n: formatCount(accounts.toDelete) })}{' '}
                  {t('deleteOrg.accountsKept', { count: accounts.toKeep, n: formatCount(accounts.toKeep) })}
                </p>
              )}
              {you?.outcome === 'deleted' && !isPreviewLoading && (
                <p className="mt-1 text-gray-700 dark:text-gray-300">{t('deleteOrg.yourAccountDeleted')}</p>
              )}
            </>
          )}
        </section>

        <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <p className="text-sm text-gray-700 dark:text-gray-300">
            <strong>{t('deleteOrg.organization')}:</strong> {organizationName}
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            {organizationEmail || t('deleteOrg.noEmailAvailable')}
          </p>
        </div>

        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('deleteOrg.typeNameToConfirm', { name: organizationName })}
            </label>
            <Input
              value={confirmationInput}
              onChange={setConfirmationInput}
              placeholder={organizationName}
              disabled={isDeleting}
              className="font-mono"
            />
          </div>
          {confirmationInput && !isConfirmationValid && (
            <p className="text-sm text-red-600 dark:text-red-400">
              {t('deleteOrg.nameMismatch')}
            </p>
          )}
          {errorMessage && (
            <p className="text-sm text-red-600 dark:text-red-400">{errorMessage}</p>
          )}
        </div>
      </div>
    </BaseModal>
  );
};
